import { CloseOutlineIcon } from '@carboncopyco/icons';
import { Button } from '@carboncopyco/ui';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { useEffect, useState } from 'react';
import { ResizableBox } from 'react-resizable';
import { ResizeHandler } from './ResizeHandler';

const transition = {
  type: 'spring',
  stiffness: 500,
  damping: 100,
};

export interface DrawerProps {
  width?: number;
  open?: boolean;
  children: React.ReactNode;
  detailsContent?: React.ReactNode;
  side?: 'left' | 'right';
  resizable?: boolean;
  maxWidth?: number;
  minWidth?: number;
  onClickOutside?: () => void;
  onCloseDetails?: () => void;
}

export const Drawer = ({
  width: initalWidth = 400,
  open,
  children,
  detailsContent,
  side,
  resizable = true,
  minWidth,
  maxWidth,
  onClickOutside,
  onCloseDetails,
}: DrawerProps) => {
  const [width, setWidth] = useState(initalWidth);

  useEffect(() => {
    if (!open) {
      setWidth(initalWidth);
    }
  }, [open, initalWidth]);

  return (
    <AnimatePresence initial={false}>
      {open && (
        <div className="flex h-full justify-end">
          {!detailsContent && (
            <div
              className="w-screen h-screen bg-transparent pointer-events-auto z-10 absolute top-0 left-0"
              onClick={onClickOutside}
            />
          )}
          <motion.div
            style={{ width }}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={transition}
            className={classNames(
              'top-0 bottom-0 bg-ds-accent3-100 absolute z-10 h-full shadow-ds-lg',
              side === 'left' ? 'left-0' : 'right-0'
            )}
            variants={{
              visible: { x: 0 },
              hidden: {
                x: side === 'left' ? -width : width,
              },
            }}
          >
            <ResizableBox
              width={width}
              onResize={(_, { size }) => setWidth(size.width)}
              className="h-full w-full relative"
              minConstraints={[minWidth ?? initalWidth, 0]}
              maxConstraints={[maxWidth ?? 600, 0]}
              axis="x"
              handle={resizable && <ResizeHandler />}
            >
              {children}
            </ResizableBox>
          </motion.div>
          {detailsContent && (
            <div
              className="bg-ds-accent3-100 relative border-l border-solid border-ds-natural-150 z-10"
              style={{
                width: `calc(100% - ${width}px)`,
              }}
            >
              {detailsContent}
              <div className="absolute top-4 right-4">
                <Button
                  linkLight
                  onClick={onCloseDetails}
                >
                  <CloseOutlineIcon size={20} />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </AnimatePresence>
  );
};
